/**
 * 맥스매치 Lite 상세 — 레거시 `maxMatchMiniDetail.js` 포팅.
 */
import type { MaxMatchMiniMr } from '@/entities/max-match/api/max-match.api';
import {
  useAddMaxMatchMiniMr,
  useAddMaxMatchMiniMrsBatch,
  useConfirmMaxMatchMiniBatch,
  useConfirmMaxMatchMiniImage,
  useDeleteAllMaxMatchMiniMrs,
  useDeleteMaxMatchMiniMr,
  useMarkMaxMatchMiniImageUnverifiable,
  useMaxMatchMiniImages,
  useRunMaxMatchMini,
  useUpdateMaxMatchMiniMr,
  useUpdateMaxMatchMiniPrescriptionInfo,
} from '@/entities/max-match/hooks/useMaxMatch';
import { ocrService } from '@/entities/ocr/api/ocr.api';
import { preprocessService } from '@/entities/ocr/api/preprocess.api';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MonthPicker } from '@/shared/components/DatePicker';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  OcrCropWarpUploader,
  type OcrCropWarpFileItem,
  type OcrCropWarpSelection,
} from '@/shared/components/OcrCropWarpUploader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { CardContent } from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { EditedOcrComparisonModal } from '@/pages/prescriptions/EditedOcrComparisonModal';
import {
  normalizeEditedOcrItems,
  type EditedOcrDrugRow,
} from '@/pages/prescriptions/prescriptionOcrUtils';
import type { DrugMatchingResultItem } from '@/pages/prescriptions/prescriptionStagingUtils';
import {
  MaxMatchDrugInlineTable,
  type MaxMatchDrugEditDraft,
} from '@/pages/max-match/MaxMatchDrugInlineTable';
import {
  applyMiniDrugMatchingResults,
  getDuplicatedDrugGroups,
  getMrRowKey,
  mapEditedOcrRowsToMrs,
  mergeDrugsByCode,
  withMiniTempRowKey,
} from '@/pages/max-match/maxMatchMiniUtils';
import {
  canAdminConfirmImage,
  canMarkImageUnverifiable,
  imageReviewBadgeLabel,
  imageSelectUnverifiableClassName,
  hasLiteMaxMatchCreatedAt,
  isImageReviewed,
  isMaxMatchMiniLockedStatus,
  isUnverifiableStatus,
  resolveMiniImageReviewStatus,
  reviewFinalizeToast,
} from '@/pages/max-match/maxMatchReviewStatus';
import {
  getBoundingBoxFromPoints,
  getInitialOcrCornerPoints,
} from '@/shared/utils/ocrCropWarpUtils';
import type { Point2D } from '@/shared/utils/ocrImagePreprocess';
import {
  buildOcrCoordinateStorageKey,
  loadOcrCoordinatesFromSession,
  OCR_COORDINATE_STORAGE_PREFIX,
  saveOcrCoordinatesToSession,
} from '@/shared/lib/ocrCoordinateStorage';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { formatDateTime } from '@/shared/lib/dateFormat';
import { useUIStore } from '@/stores/uiStore';
import {
  useNavigate,
  useParams,
  useRouter,
  useSearch,
} from '@tanstack/react-router';
import {
  Check,
  CircleOff,
  Download,
  ExternalLink,
  Play,
  ScanLine,
  Sparkles,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { maxMatchService } from '@/entities/max-match/api/max-match.api';
import { FooterRow, ToolbarRow } from '@/shared/components/ui/row';
import {
  coerceSearchString,
  loadMaxMatchMiniHeader,
  pickNonEmpty,
  saveMaxMatchMiniHeader,
  type MaxMatchMiniHeaderPayload,
} from '@/pages/max-match/maxMatchMiniHeader';
import {
  downloadUriFile,
  openFilePreview,
} from '@/shared/components/DownloadWithPreview';

type ConfirmKind =
  | 'deleteAll'
  | 'ocrOverwrite'
  | 'editedOcrOverwrite'
  | 'leave'
  | 'unverifiable'
  | null;

const MINI_DETAIL_COPY = {
  loading: '맥스매치 Lite 상세를 불러오는 중...',
  title: 'max match lite 처방 내역 확정',
  listPath: ROUTE_PATHS.MAX_MATCH_MINI,
} as const;

/**
 * 상단 헤더 — 목록→상세 search / sessionStorage 만 사용.
 * prescription-info 는 PUT(처방년월 수정)만 존재하므로 GET 폴백을 두지 않는다.
 */
const resolveMiniHeader = (
  summaryId: number,
  routeSearch: Record<string, unknown>,
): MaxMatchMiniHeaderPayload => {
  const stored = loadMaxMatchMiniHeader(summaryId);
  return {
    email: pickNonEmpty(coerceSearchString(routeSearch.email), stored?.email),
    companyName: pickNonEmpty(
      coerceSearchString(routeSearch.companyName),
      stored?.companyName,
    ),
    createdAt: pickNonEmpty(
      coerceSearchString(routeSearch.createdAt),
      stored?.createdAt,
    ),
    maxMatchCreatedAt:
      pickNonEmpty(
        coerceSearchString(routeSearch.maxMatchCreatedAt),
        stored?.maxMatchCreatedAt,
      ) || '-',
    prescriptionYearMonth: pickNonEmpty(
      coerceSearchString(routeSearch.prescriptionYearMonth),
      stored?.prescriptionYearMonth,
    ),
    status: pickNonEmpty(
      coerceSearchString(routeSearch.status),
      stored?.status,
    ),
  };
};

export const MaxMatchMiniDetailPage: React.FC = () => {
  const copy = MINI_DETAIL_COPY;
  const toast = useToast();
  const { readOnly } = useMenuAccess();
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const navigate = useNavigate();
  const router = useRouter();
  const { summaryId: summaryIdParam } = useParams({ strict: false }) as {
    summaryId?: string;
  };
  const summaryId = Number(summaryIdParam ?? 0);
  const routeSearch = useSearch({ strict: false }) as Record<string, unknown>;

  const {
    data: images = [],
    isLoading: imagesLoading,
    refetch: refetchImages,
  } = useMaxMatchMiniImages(summaryId);

  const [selectedImageId, setSelectedImageId] = useState(0);
  const [drugData, setDrugData] = useState<MaxMatchMiniMr[]>([]);
  const [drugLoading, setDrugLoading] = useState(false);
  const [prescriptionYm, setPrescriptionYm] = useState('');
  const [status, setStatus] = useState('');
  const [isYearMonthChanged, setIsYearMonthChanged] = useState(false);
  const [plainOcrPending, setPlainOcrPending] = useState(false);
  const [headerRevision, setHeaderRevision] = useState(0);
  const [pendingEditedOcrSource, setPendingEditedOcrSource] = useState<
    'default' | 'gemini' | null
  >(null);

  const header = useMemo(
    () => resolveMiniHeader(summaryId, routeSearch),
    [summaryId, routeSearch, headerRevision],
  );

  const persistHeader = (patch: Partial<MaxMatchMiniHeaderPayload>) => {
    saveMaxMatchMiniHeader(summaryId, { ...header, ...patch });
    setHeaderRevision((n) => n + 1);
  };

  const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
  const [ocrFileDialogOpen, setOcrFileDialogOpen] = useState(false);
  const [editedOcrImages, setEditedOcrImages] = useState<OcrCropWarpFileItem[]>(
    [],
  );
  const [ocrLiveSelection, setOcrLiveSelection] =
    useState<OcrCropWarpSelection | null>(null);
  const [ocrSourceInitialPoints, setOcrSourceInitialPoints] = useState<
    Point2D[]
  >([]);
  const [isPreparingOcrImageEditor, setIsPreparingOcrImageEditor] =
    useState(false);
  const [showDuplicateMergeModal, setShowDuplicateMergeModal] = useState(false);
  const [drugMatching, setDrugMatching] = useState(false);
  const [editedOcrComparing, setEditedOcrComparing] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<{
    default: EditedOcrDrugRow[];
    gemini: EditedOcrDrugRow[];
  }>({ default: [], gemini: [] });
  const [editedOcrRequestPreviewUrl, setEditedOcrRequestPreviewUrl] = useState<
    string | null
  >(null);
  const ocrCoordinatesFetchKeyRef = useRef<string | null>(null);

  const confirmImage = useConfirmMaxMatchMiniImage();
  const markUnverifiable = useMarkMaxMatchMiniImageUnverifiable();
  const confirmBatch = useConfirmMaxMatchMiniBatch();
  const addMr = useAddMaxMatchMiniMr();
  const addMrsBatch = useAddMaxMatchMiniMrsBatch();
  const deleteMr = useDeleteMaxMatchMiniMr();
  const deleteAllMrs = useDeleteAllMaxMatchMiniMrs();
  const updateMr = useUpdateMaxMatchMiniMr();
  const updatePrescriptionInfo = useUpdateMaxMatchMiniPrescriptionInfo();
  const runMini = useRunMaxMatchMini();

  const selectedImage = useMemo(
    () => images.find((img) => img.imageId === selectedImageId) ?? null,
    [images, selectedImageId],
  );

  // 레거시 max-match-mini-ocr-coordinates sessionStorage 복원
  useEffect(() => {
    const sourceImageUrl = selectedImage?.imageUrl;
    const storageKey = buildOcrCoordinateStorageKey(
      selectedImage?.imageCode,
      OCR_COORDINATE_STORAGE_PREFIX.maxMatchMini,
    );
    const stored = loadOcrCoordinatesFromSession(storageKey, sourceImageUrl);
    if (stored) {
      setOcrSourceInitialPoints(stored.points);
      ocrCoordinatesFetchKeyRef.current = sourceImageUrl ?? null;
    } else {
      setOcrSourceInitialPoints([]);
      ocrCoordinatesFetchKeyRef.current = null;
    }
  }, [selectedImage?.imageCode, selectedImage?.imageUrl]);

  useEffect(() => {
    const sourceImageUrl = selectedImage?.imageUrl;
    if (
      !sourceImageUrl ||
      ocrSourceInitialPoints.length !== 4 ||
      !selectedImage?.imageCode
    ) {
      return;
    }
    const storageKey = buildOcrCoordinateStorageKey(
      selectedImage.imageCode,
      OCR_COORDINATE_STORAGE_PREFIX.maxMatchMini,
    );
    saveOcrCoordinatesToSession(storageKey, {
      sourceImageUrl,
      points: ocrSourceInitialPoints,
      bbox: getBoundingBoxFromPoints(ocrSourceInitialPoints),
    });
  }, [
    ocrSourceInitialPoints,
    selectedImage?.imageCode,
    selectedImage?.imageUrl,
  ]);

  useEffect(() => {
    const busy =
      isPreparingOcrImageEditor ||
      plainOcrPending ||
      editedOcrComparing ||
      drugMatching;
    setGlobalLoading(
      busy,
      isPreparingOcrImageEditor
        ? '편집 영역을 준비하는 중...'
        : drugMatching
          ? '의약품 매칭 요청 중...'
          : editedOcrComparing
            ? '편집 이미지 OCR 비교 요청 중...'
            : 'OCR 요청 중...',
    );
    return () => setGlobalLoading(false);
  }, [
    isPreparingOcrImageEditor,
    plainOcrPending,
    editedOcrComparing,
    drugMatching,
    setGlobalLoading,
  ]);

  const reviewedCount = useMemo(
    () =>
      images.filter((img) => isImageReviewed(resolveMiniImageReviewStatus(img)))
        .length,
    [images],
  );

  const confirmableCount = useMemo(
    () =>
      images.filter((img) =>
        canAdminConfirmImage(resolveMiniImageReviewStatus(img)),
      ).length,
    [images],
  );

  const hasUnsavedDrugs = useMemo(
    () => drugData.some((item) => item.mrId == null),
    [drugData],
  );

  const duplicatedDrugGroups = useMemo(
    () => getDuplicatedDrugGroups(drugData),
    [drugData],
  );

  // ADMIN_CONFIRMED 는 이미지 검수 완료일 수 있어 잠그지 않는다.
  // 이미 생성한 건은 생성일(maxMatchCreatedAt)로만 막는다.
  const isMatched =
    isMaxMatchMiniLockedStatus(status) ||
    hasLiteMaxMatchCreatedAt(header.maxMatchCreatedAt);

  useEffect(() => {
    if (header.prescriptionYearMonth) {
      setPrescriptionYm(header.prescriptionYearMonth);
    }
    if (header.status) {
      setStatus(header.status);
    }
  }, [header.prescriptionYearMonth, header.status]);

  useEffect(() => {
    if (images.length > 0 && !selectedImageId) {
      setSelectedImageId(images[0].imageId);
    }
  }, [images, selectedImageId]);

  const fetchMrsData = useCallback(async (imageId: number) => {
    if (!imageId) return;
    setDrugLoading(true);
    try {
      const data = await maxMatchService.miniMrs(imageId);
      setDrugData(data.map(withMiniTempRowKey));
    } catch (e) {
      handleApiError(e);
    } finally {
      setDrugLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedImageId) {
      void fetchMrsData(selectedImageId);
    }
  }, [selectedImageId, fetchMrsData]);

  const handleDeleteAllMrs = async () => {
    if (!selectedImageId) return;
    try {
      await deleteAllMrs.mutateAsync(selectedImageId);
      setDrugData([]);
      await refetchImages();
      toast.success('의약품이 삭제되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setConfirmKind(null);
    }
  };

  /** 레거시 runPlainOcr — Document AI process-url */
  const fetchPlainOcrMrs = async (): Promise<MaxMatchMiniMr[]> => {
    const raw = await ocrService.ocrPrescriptionFromUrl({
      imageUrl: selectedImage!.imageUrl!,
    });
    const rows = normalizeEditedOcrItems(raw);
    if (rows.length === 0) {
      throw new Error('OCR 결과가 없습니다.');
    }
    return mapEditedOcrRowsToMrs(rows);
  };

  const runOcr = async () => {
    if (!selectedImage?.imageUrl) {
      toast.error('이미지가 선택되지 않았습니다.');
      return;
    }
    setPlainOcrPending(true);
    try {
      const drugList = await fetchPlainOcrMrs();
      setDrugData(drugList);
      toast.success('OCR 요청이 완료되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setPlainOcrPending(false);
    }
  };

  const fetchInitialOcrCoordinates = useCallback(async () => {
    const sourceImageUrl = selectedImage?.imageUrl;
    const fetchKey = sourceImageUrl || null;
    if (!fetchKey) {
      setOcrSourceInitialPoints([]);
      return false;
    }

    const storageKey = buildOcrCoordinateStorageKey(
      selectedImage?.imageCode,
      OCR_COORDINATE_STORAGE_PREFIX.maxMatchMini,
    );
    const cached = loadOcrCoordinatesFromSession(storageKey, sourceImageUrl);
    if (cached) {
      ocrCoordinatesFetchKeyRef.current = fetchKey;
      setOcrSourceInitialPoints(cached.points);
      return true;
    }

    if (ocrCoordinatesFetchKeyRef.current === fetchKey) {
      return ocrSourceInitialPoints.length === 4;
    }

    setIsPreparingOcrImageEditor(true);
    try {
      const response = await preprocessService.getCornersFromUrl(
        sourceImageUrl!,
      );
      const nextPoints = getInitialOcrCornerPoints(response);
      if (nextPoints.length === 4) {
        ocrCoordinatesFetchKeyRef.current = fetchKey;
        setOcrSourceInitialPoints(nextPoints);
        saveOcrCoordinatesToSession(storageKey, {
          sourceImageUrl: sourceImageUrl!,
          points: nextPoints,
          bbox: getBoundingBoxFromPoints(nextPoints),
        });
        return true;
      }
      setOcrSourceInitialPoints([]);
      return false;
    } catch (e) {
      console.error('Failed to prepare OCR coordinates:', e);
      toast.error(
        'OCR 좌표를 가져오지 못했습니다. 기본 영역으로 편집할 수 있습니다.',
      );
      setOcrSourceInitialPoints([]);
      return false;
    } finally {
      setIsPreparingOcrImageEditor(false);
    }
  }, [
    ocrSourceInitialPoints.length,
    selectedImage?.imageCode,
    selectedImage?.imageUrl,
    toast,
  ]);

  const handleOpenOcrImageEditor = useCallback(async () => {
    if (!selectedImage?.imageUrl) {
      toast.error('편집할 원본 이미지가 없습니다.');
      return;
    }
    setEditedOcrImages([]);
    await fetchInitialOcrCoordinates();
    setOcrFileDialogOpen(true);
  }, [fetchInitialOcrCoordinates, selectedImage?.imageUrl, toast]);

  /** 레거시/처방검수: 닫을 때 편집 corners를 다음 오픈용 source로 재반영 */
  const closeOcrImageEditor = useCallback(() => {
    const meta = editedOcrImages[0]?.ocrMeta;
    const live = ocrLiveSelection;
    const corners = meta?.corners?.length === 4 ? meta.corners : live?.corners;
    const bbox =
      meta?.corners?.length === 4
        ? (meta.bbox ?? getBoundingBoxFromPoints(meta.corners))
        : live?.bbox;
    if (corners?.length === 4) {
      setOcrSourceInitialPoints(corners);
      if (selectedImage?.imageUrl) {
        const storageKey = buildOcrCoordinateStorageKey(
          selectedImage.imageCode || String(selectedImage.imageId),
          OCR_COORDINATE_STORAGE_PREFIX.maxMatchMini,
        );
        saveOcrCoordinatesToSession(storageKey, {
          sourceImageUrl: selectedImage.imageUrl,
          points: corners,
          bbox: bbox ?? getBoundingBoxFromPoints(corners),
        });
      }
    }
    setOcrFileDialogOpen(false);
    setEditedOcrImages([]);
    setOcrLiveSelection(null);
  }, [editedOcrImages, ocrLiveSelection, selectedImage]);

  const handleOcr = async () => {
    if (drugData.length > 0) {
      setConfirmKind('ocrOverwrite');
      return;
    }
    await runOcr();
  };

  const runEditedOcrComparison = async () => {
    const imageUrl = selectedImage?.imageUrl;
    const meta = editedOcrImages[0]?.ocrMeta;
    const corners = meta?.corners ?? ocrLiveSelection?.corners;
    const bbox = meta?.bbox ?? ocrLiveSelection?.bbox;
    const nextPreviewUrl =
      editedOcrImages[0]?.preview || ocrLiveSelection?.previewUrl || '';

    if (!imageUrl || !bbox || !Array.isArray(corners) || corners.length !== 4) {
      toast.error('OCR 좌표 영역을 먼저 선택해 주세요.');
      return;
    }

    setEditedOcrComparing(true);
    try {
      let previewForModal = nextPreviewUrl || null;
      if (nextPreviewUrl?.startsWith('blob:')) {
        try {
          const blob = await (await fetch(nextPreviewUrl)).blob();
          previewForModal = URL.createObjectURL(blob);
        } catch {
          previewForModal = null;
        }
      }

      const payload = { imageUrl, corners, bbox };
      const [defaultResult, geminiResult] = await Promise.allSettled([
        ocrService.ocrPrescriptionFromUrl(payload),
        ocrService.ocrPrescriptionFromUrlGemini(payload),
      ]);

      const normalizedDefault =
        defaultResult.status === 'fulfilled'
          ? normalizeEditedOcrItems(defaultResult.value)
          : [];
      const normalizedGemini =
        geminiResult.status === 'fulfilled'
          ? normalizeEditedOcrItems(geminiResult.value)
          : [];

      if (normalizedDefault.length === 0 && normalizedGemini.length === 0) {
        if (previewForModal?.startsWith('blob:')) {
          URL.revokeObjectURL(previewForModal);
        }
        throw new Error(
          'Document AI와 Gemini OCR 결과를 모두 가져오지 못했습니다.',
        );
      }

      setEditedOcrRequestPreviewUrl((prev) => {
        if (prev?.startsWith('blob:') && prev !== previewForModal) {
          URL.revokeObjectURL(prev);
        }
        return previewForModal;
      });
      setComparisonResults({
        default: normalizedDefault,
        gemini: normalizedGemini,
      });
      setComparisonOpen(true);
      setOcrFileDialogOpen(false);
      setEditedOcrImages([]);
      setOcrLiveSelection(null);

      if (
        defaultResult.status === 'rejected' ||
        geminiResult.status === 'rejected'
      ) {
        toast.error(
          '일부 OCR 요청이 실패했습니다. 조회된 결과만 비교 후 선택해 주세요.',
        );
      } else {
        toast.success('OCR 비교 결과를 불러왔습니다.');
      }
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setEditedOcrComparing(false);
    }
  };

  const applyEditedOcrRows = (source: 'default' | 'gemini') => {
    const selected = comparisonResults[source] ?? [];
    setDrugData(mapEditedOcrRowsToMrs(selected));
    setComparisonOpen(false);
    setPendingEditedOcrSource(null);
    toast.success(
      `${source === 'gemini' ? 'Gemini' : 'Document AI'} OCR 결과를 반영했습니다.`,
    );
  };

  const applyEditedOcrResult = async (source: 'default' | 'gemini') => {
    const selected = comparisonResults[source] ?? [];
    if (!selected.length) {
      toast.error('적용할 OCR 결과가 없습니다.');
      return;
    }

    // 레거시: 기존 약이 있으면 확인 후 삭제·적용
    if (drugData.length > 0 && selectedImageId) {
      setPendingEditedOcrSource(source);
      setConfirmKind('editedOcrOverwrite');
      return;
    }

    applyEditedOcrRows(source);
  };

  const submitDrugBatch = async (rows: MaxMatchMiniMr[]) => {
    if (!selectedImageId) return;
    try {
      await addMrsBatch.mutateAsync({
        imageId: selectedImageId,
        payload: rows.map((item) => ({
          drugCode: item.drugCode,
          outHospitalQuantity: item.outHospitalQuantity ?? 0,
          inHospitalQuantity: item.inHospitalQuantity ?? 0,
        })),
      });
      await fetchMrsData(selectedImageId);
      toast.success('의약품이 등록되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleBatchAdd = async () => {
    if (!selectedImageId) return;
    const pendingDrugs = drugData.filter((item) => item.mrId == null);
    if (pendingDrugs.length === 0) {
      toast.error('일괄 등록할 의약품이 없습니다.');
      return;
    }
    if (pendingDrugs.some((item) => !item.drugCode)) {
      toast.error('의약품 코드가 비어있는 의약품이 있습니다.');
      return;
    }
    if (duplicatedDrugGroups.length > 0) {
      setShowDuplicateMergeModal(true);
      return;
    }
    await submitDrugBatch(pendingDrugs);
  };

  const handleConfirmDuplicateMerge = async () => {
    setShowDuplicateMergeModal(false);
    const pendingDrugs = drugData.filter((item) => item.mrId == null);
    await submitDrugBatch(mergeDrugsByCode(pendingDrugs));
  };

  const handleDrugMatching = async () => {
    if (drugData.length === 0) {
      toast.error('매칭할 의약품이 없습니다.');
      return;
    }
    if (!prescriptionYm) {
      toast.error('처방 년 월 정보가 없습니다.');
      return;
    }

    setDrugMatching(true);
    try {
      const items = drugData.map((item) => ({
        drugName: item.drugName ?? '',
        ...(item.drugCode ? { drugCode: item.drugCode } : {}),
        ...(item.pharmaCompanyName
          ? { pharmaCompanyName: item.pharmaCompanyName }
          : {}),
        ...(item.price != null ? { unitPrice: item.price } : {}),
        ...(item.inHospitalQuantity != null
          ? { inQuantity: item.inHospitalQuantity }
          : {}),
        ...(item.outHospitalQuantity != null
          ? { outQuantity: item.outHospitalQuantity }
          : {}),
      }));

      const response = await ocrService.searchDrugMatching({
        items,
        limit: 5,
        yearMonth: prescriptionYm,
      });

      if (response.metadata?.confidence === 'no_data') {
        toast.error(
          response.metadata.reason ||
            '해당 연월의 마스터 데이터가 없어 매칭할 수 없습니다.',
        );
        return;
      }

      const results = response.results as DrugMatchingResultItem[];
      if (results.length === 0) {
        toast.error('매칭 결과가 없습니다.');
        return;
      }

      const { nextList, matchedCount } = applyMiniDrugMatchingResults(
        drugData,
        results,
      );
      setDrugData(nextList);
      toast.success(`의약품 매칭 완료 (${matchedCount}/${drugData.length}건)`);
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setDrugMatching(false);
    }
  };

  /** 처방년월이 바뀌었으면 prescription-info 저장 후 확정에 넘긴다 */
  const persistPrescriptionYearMonthIfNeeded = async (): Promise<boolean> => {
    if (!summaryId || !prescriptionYm) return false;
    if (!isYearMonthChanged) return true;
    await updatePrescriptionInfo.mutateAsync({
      summaryId,
      prescriptionYearMonth: prescriptionYm,
    });
    setIsYearMonthChanged(false);
    persistHeader({ prescriptionYearMonth: prescriptionYm });
    return true;
  };

  const syncMiniSummaryStatus = async () => {
    const yearMonth = prescriptionYm || header.prescriptionYearMonth || '';
    if (!yearMonth) return null;
    const summary = await maxMatchService.findMiniSummary(
      summaryId,
      yearMonth,
      header.email,
    );
    if (summary?.status) {
      setStatus(summary.status);
      persistHeader({ status: summary.status });
    }
    return summary;
  };

  const handleConfirmImage = async () => {
    if (!selectedImageId) return;
    if (
      !canAdminConfirmImage(resolveMiniImageReviewStatus(selectedImage ?? {}))
    ) {
      toast.error('이미 검수가 끝난 이미지입니다.');
      return;
    }
    if (hasUnsavedDrugs) {
      toast.error('의약품 정보가 저장되지 않은 의약품이 있습니다.');
      return;
    }
    if (
      drugData.length > 0 &&
      !drugData.every((item) => item.hasDrugInfo === true)
    ) {
      toast.error('의약품 정보가 존재하지 않는 의약품이 있습니다.');
      return;
    }
    try {
      await persistPrescriptionYearMonthIfNeeded();
      await confirmImage.mutateAsync(selectedImageId);
      const { data: nextImages } = await refetchImages();
      const summary = await syncMiniSummaryStatus();
      if (summary?.status === 'UNVERIFIABLE') {
        toast.success(reviewFinalizeToast(summary.status));
      } else {
        const nextImage = nextImages?.find(
          (img) => img.imageId === selectedImageId,
        );
        toast.success(
          isUnverifiableStatus(resolveMiniImageReviewStatus(nextImage ?? {}))
            ? '확인불가로 처리되었습니다.'
            : reviewFinalizeToast(summary?.status),
        );
      }
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleMarkUnverifiable = async () => {
    if (!selectedImageId) return;
    if (
      !canMarkImageUnverifiable(
        resolveMiniImageReviewStatus(selectedImage ?? {}),
      )
    ) {
      toast.error('이미 검수가 끝난 이미지입니다.');
      return;
    }
    try {
      await markUnverifiable.mutateAsync(selectedImageId);
      await refetchImages();
      const summary = await syncMiniSummaryStatus();
      toast.success(
        summary?.status === 'UNVERIFIABLE'
          ? '내역 확인불가로 처리되었습니다.'
          : '확인불가로 처리되었습니다.',
      );
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleConfirmAll = async () => {
    if (hasUnsavedDrugs) {
      toast.error('의약품 정보가 저장되지 않은 의약품이 있습니다.');
      return;
    }
    const imageIds = images
      .filter((img) => canAdminConfirmImage(resolveMiniImageReviewStatus(img)))
      .map((img) => img.imageId);
    if (imageIds.length === 0) {
      toast.error('확정할 이미지가 없습니다.');
      return;
    }
    try {
      await persistPrescriptionYearMonthIfNeeded();
      await confirmBatch.mutateAsync(imageIds);
      await refetchImages();
      const summary = await syncMiniSummaryStatus();
      toast.success(
        summary?.status === 'UNVERIFIABLE'
          ? reviewFinalizeToast(summary.status)
          : '전체 확정이 완료되었습니다.',
      );
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleRunMaxMatch = async () => {
    try {
      await runMini.mutateAsync(summaryId);
      persistHeader({
        maxMatchCreatedAt: new Date().toISOString(),
      });
      toast.success('max match 생성이 완료되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const refreshMrsKeepingUnsaved = async (excludeRowKey?: string | number) => {
    if (!selectedImageId) return;
    const remainingUnsaved = drugData.filter((item) => {
      if (item.mrId != null) return false;
      if (excludeRowKey == null) return true;
      return getMrRowKey(item) !== excludeRowKey;
    });
    await fetchMrsData(selectedImageId);
    if (remainingUnsaved.length > 0) {
      setDrugData((prev) => [...prev, ...remainingUnsaved]);
    }
  };

  const handlePersistSavedRow = async (
    row: MaxMatchMiniMr,
    draft: MaxMatchDrugEditDraft,
  ) => {
    if (row.mrId == null || !selectedImageId) return;
    await updateMr.mutateAsync({
      mrId: row.mrId,
      payload: {
        drugCode: draft.drugCode.trim(),
        drugName: draft.drugName || '',
        outHospitalQuantity: draft.outHospitalQuantity,
        inHospitalQuantity: draft.inHospitalQuantity,
      },
    });
    await refreshMrsKeepingUnsaved();
  };

  const handlePersistStagingRow = async (
    row: MaxMatchMiniMr,
    draft: MaxMatchDrugEditDraft,
  ) => {
    if (!selectedImageId) return;
    await addMr.mutateAsync({
      imageId: selectedImageId,
      payload: {
        drugCode: draft.drugCode.trim(),
        outHospitalQuantity: draft.outHospitalQuantity,
        inHospitalQuantity: draft.inHospitalQuantity,
      },
    });
    // 방금 저장한 staging 행은 서버 refetch 결과에 포함되므로 로컬 잔여에서 제외
    await refreshMrsKeepingUnsaved(getMrRowKey(row));
  };

  const handleDeleteSavedRow = async (row: MaxMatchMiniMr) => {
    if (row.mrId == null || !selectedImageId) return;
    await deleteMr.mutateAsync(row.mrId);
    await refreshMrsKeepingUnsaved();
  };

  const handlePersistLookup = async (
    row: MaxMatchMiniMr,
    draft: MaxMatchDrugEditDraft,
  ) => {
    if (row.mrId == null || !selectedImageId) return;
    await updateMr.mutateAsync({
      mrId: row.mrId,
      payload: {
        drugCode: draft.drugCode.trim(),
        drugName: draft.drugName || '',
        outHospitalQuantity: draft.outHospitalQuantity,
        inHospitalQuantity: draft.inHospitalQuantity,
      },
    });
    await refreshMrsKeepingUnsaved();
  };

  const isOcrBusy =
    plainOcrPending ||
    editedOcrComparing ||
    drugMatching ||
    deleteAllMrs.isPending;

  if (imagesLoading) {
    return <LoadingSpinner tip={copy.loading} />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Sparkles}
        title={copy.title}
        description={`요약 #${summaryId}`}
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              className="min-w-[7.5rem]"
              onClick={() => {
                if (isYearMonthChanged || hasUnsavedDrugs) {
                  setConfirmKind('leave');
                  return;
                }
                if (window.history.length > 1) {
                  router.history.back();
                } else {
                  navigate({ to: copy.listPath } as never);
                }
              }}
            >
              나가기
            </Button>
            <MenuWriteGate>
              <Button
                size="sm"
                className="ruda-button min-w-[7.5rem]"
                disabled={
                  readOnly ||
                  images.length === 0 ||
                  confirmableCount === 0 ||
                  hasUnsavedDrugs ||
                  isMatched
                }
                onClick={handleConfirmAll}
              >
                전체 확정 완료
              </Button>
            </MenuWriteGate>
          </>
        }
      />

      <DataCard>
        <CardContent className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label>사용자 이메일</Label>
            <Input value={header.email} disabled readOnly />
          </div>
          <div className="space-y-1">
            <Label>업체명</Label>
            <Input value={header.companyName} disabled readOnly />
          </div>
          <div className="space-y-1">
            <Label>등록일시</Label>
            <Input value={formatDateTime(header.createdAt)} disabled readOnly />
          </div>
          <div className="space-y-1">
            <Label>처방 년월</Label>
            <MonthPicker
              value={prescriptionYm}
              disabled={readOnly}
              onChange={(v) => {
                setPrescriptionYm(v);
                setIsYearMonthChanged(true);
              }}
              placeholder="년월 선택"
            />
          </div>
          <div className="space-y-1 sm:col-span-2 lg:col-span-2">
            <Label>max match 생성일시</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={formatDateTime(header.maxMatchCreatedAt)}
                disabled
                readOnly
                className="min-w-0 flex-1"
              />
              <MenuWriteGate>
                <Button
                  size="sm"
                  className="ruda-button shrink-0"
                  disabled={
                    readOnly ||
                    images.length === 0 ||
                    reviewedCount !== images.length ||
                    isMatched
                  }
                  onClick={handleRunMaxMatch}
                >
                  <Play className="mr-2 h-4 w-4" />
                  max match 생성
                </Button>
              </MenuWriteGate>
            </div>
          </div>
        </CardContent>
      </DataCard>

      <FilterCard>
        <ToolbarRow justify="between">
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-destructive">처방 이미지 선택</Label>
            <Select
              value={selectedImageId ? String(selectedImageId) : ''}
              onValueChange={(v) => setSelectedImageId(Number(v))}
            >
              <SelectTrigger
                className={cn(
                  'w-72',
                  imageSelectUnverifiableClassName(
                    resolveMiniImageReviewStatus(selectedImage ?? {}),
                  ),
                )}
              >
                <SelectValue placeholder="처방 이미지 선택" />
              </SelectTrigger>
              <SelectContent>
                {images.map((img) => {
                  const reviewStatus = resolveMiniImageReviewStatus(img);
                  return (
                    <SelectItem
                      key={img.imageId}
                      value={String(img.imageId)}
                      className={imageSelectUnverifiableClassName(reviewStatus)}
                    >
                      {img.imageCode ?? `이미지 ${img.imageId}`} (
                      {imageReviewBadgeLabel(reviewStatus)})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {selectedImage?.imageUrl && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openFilePreview(selectedImage.imageUrl!)}
                >
                  <ExternalLink className="mr-1 h-3 w-3" />
                  보기
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void downloadUriFile(
                      selectedImage.imageUrl!,
                      selectedImage.imageCode || String(selectedImage.imageId),
                    ).catch((e) => handleApiError(e, toast));
                  }}
                >
                  <Download className="mr-1 h-3 w-3" />
                  다운로드
                </Button>
              </>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            검수/등록 이미지 수:{' '}
            <span className="font-medium text-foreground">
              {reviewedCount} / {images.length}
            </span>
          </div>
        </ToolbarRow>
      </FilterCard>

      {images.length === 0 ? (
        <EmptyState description="등록된 처방 이미지가 없습니다." />
      ) : (
        <>
          <MaxMatchDrugInlineTable
            rows={drugData}
            loading={drugLoading}
            readOnly={readOnly}
            disabled={isMatched}
            refYm={prescriptionYm}
            onChangeRows={setDrugData}
            onPersistSavedRow={handlePersistSavedRow}
            onPersistStagingRow={handlePersistStagingRow}
            onDeleteSavedRow={handleDeleteSavedRow}
            onPersistLookup={handlePersistLookup}
          />

          <ToolbarRow justify="between" gap={16}>
            <MenuWriteGate>
              <ToolbarRow gap={8}>
                <Button
                  className="ruda-button"
                  disabled={isMatched || isOcrBusy || readOnly}
                  onClick={handleOcr}
                >
                  <ScanLine className="mr-2 h-4 w-4" />
                  OCR
                </Button>
                <Button
                  variant="outline"
                  disabled={
                    isMatched ||
                    !selectedImage ||
                    isPreparingOcrImageEditor ||
                    readOnly
                  }
                  onClick={handleOpenOcrImageEditor}
                >
                  {isPreparingOcrImageEditor
                    ? '좌표 준비 중...'
                    : '이미지 편집 OCR'}
                </Button>
                <Button
                  className="ruda-button"
                  disabled={
                    isMatched || !hasUnsavedDrugs || drugMatching || readOnly
                  }
                  onClick={() => void handleDrugMatching()}
                >
                  의약품 매칭
                </Button>
                <Button
                  variant="outline"
                  disabled={isMatched || !hasUnsavedDrugs || readOnly}
                  onClick={() => void handleBatchAdd()}
                >
                  의약품 일괄 추가
                </Button>
              </ToolbarRow>
            </MenuWriteGate>
            <MenuWriteGate>
              <ToolbarRow gap={8}>
                <Button
                  variant="outline"
                  disabled={
                    readOnly ||
                    isMatched ||
                    markUnverifiable.isPending ||
                    !canMarkImageUnverifiable(
                      resolveMiniImageReviewStatus(selectedImage ?? {}),
                    )
                  }
                  onClick={() => setConfirmKind('unverifiable')}
                >
                  <CircleOff className="mr-2 h-4 w-4" />
                  확인불가
                </Button>
                <Button
                  className="ruda-button"
                  disabled={
                    readOnly ||
                    hasUnsavedDrugs ||
                    isMatched ||
                    !canAdminConfirmImage(
                      resolveMiniImageReviewStatus(selectedImage ?? {}),
                    )
                  }
                  onClick={() => void handleConfirmImage()}
                >
                  <Check className="mr-2 h-4 w-4" />
                  확정
                </Button>
              </ToolbarRow>
            </MenuWriteGate>
          </ToolbarRow>
        </>
      )}

      <Dialog
        open={ocrFileDialogOpen}
        onOpenChange={(open) => !open && closeOcrImageEditor()}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>이미지 편집 OCR</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            선택된 처방 이미지를 편집한 뒤 OCR을 요청할 수 있습니다.
          </p>
          {isPreparingOcrImageEditor ? (
            <LoadingSpinner tip="편집 영역을 준비하는 중..." />
          ) : (
            <OcrCropWarpUploader
              maxCount={1}
              // 레거시 maxMatchMiniDetail: warp-crop + 해상도 제한 없음
              preprocessMode="warp-crop"
              maxSourceEdge={Number.POSITIVE_INFINITY}
              maxWarpWidth={Number.POSITIVE_INFINITY}
              maxWarpHeight={Number.POSITIVE_INFINITY}
              sourceImageUrl={selectedImage?.imageUrl}
              sourceImageName={`${selectedImage?.imageCode || 'prescription'}.png`}
              sourceButtonText="현재 처방 이미지 편집"
              allowFileSelect={false}
              sourceInitialPoints={ocrSourceInitialPoints}
              value={editedOcrImages}
              onChange={setEditedOcrImages}
              onSelectionChange={setOcrLiveSelection}
            />
          )}
          <FooterRow>
            <Button variant="outline" onClick={closeOcrImageEditor}>
              닫기
            </Button>
            <Button
              className="ruda-button"
              disabled={
                isPreparingOcrImageEditor ||
                editedOcrComparing ||
                !(
                  (editedOcrImages[0]?.ocrMeta?.corners?.length === 4 &&
                    editedOcrImages[0]?.ocrMeta?.bbox) ||
                  (ocrLiveSelection?.corners?.length === 4 &&
                    ocrLiveSelection?.bbox)
                )
              }
              onClick={() => void runEditedOcrComparison()}
            >
              OCR 요청 실행
            </Button>
          </FooterRow>
        </DialogContent>
      </Dialog>

      <EditedOcrComparisonModal
        open={comparisonOpen}
        onOpenChange={setComparisonOpen}
        defaultRows={comparisonResults.default}
        geminiRows={comparisonResults.gemini}
        onApply={(source) => void applyEditedOcrResult(source)}
        applying={deleteAllMrs.isPending}
        previewUrl={editedOcrRequestPreviewUrl}
      />

      <Dialog
        open={showDuplicateMergeModal}
        onOpenChange={setShowDuplicateMergeModal}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>동일 처방 의약품 확인</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            아래 의약품은 코드가 중복되어 있습니다. 사용량을 합산해 각 1건으로
            등록됩니다.
          </p>
          <div className="max-h-[420px] space-y-6 overflow-y-auto">
            {duplicatedDrugGroups.map((group) => (
              <div key={group.drugCode} className="space-y-2">
                <p className="font-medium">
                  {group.drugCode} · {group.drugName || '-'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {group.pharmaCompanyName || '-'} · 약가{' '}
                  {(group.price || 0).toLocaleString()}원
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>순번</TableHead>
                      <TableHead className="text-right">원외 사용량</TableHead>
                      <TableHead className="text-right">원내 사용량</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.rows.map((row, index) => (
                      <TableRow key={getMrRowKey(row)}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell className="text-right">
                          {Number(
                            row.outHospitalQuantity ?? 0,
                          ).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {Number(row.inHospitalQuantity ?? 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className="font-semibold">합계</TableCell>
                      <TableCell className="text-right font-semibold">
                        {group.totalOutHospitalQuantity.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {group.totalInHospitalQuantity.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDuplicateMergeModal(false)}
            >
              취소
            </Button>
            <Button
              className="ruda-button"
              onClick={() => void handleConfirmDuplicateMerge()}
            >
              합산하여 등록
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmKind != null}
        onOpenChange={(open) => !open && setConfirmKind(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmKind === 'unverifiable'
                ? '확인불가'
                : confirmKind === 'deleteAll' ||
                    confirmKind === 'ocrOverwrite' ||
                    confirmKind === 'editedOcrOverwrite'
                  ? '확인'
                  : '나가기'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmKind === 'unverifiable'
                ? '이 이미지를 확인할 수 없는 처방으로 처리할까요? 원본은 보존되며 맥스매치 계산에서는 제외됩니다.'
                : confirmKind === 'ocrOverwrite' ||
                    confirmKind === 'editedOcrOverwrite'
                  ? '의약품이 이미 등록되어 있습니다. 삭제하고 다시 등록하시겠습니까?'
                  : confirmKind === 'leave'
                    ? '저장되지 않은 정보가 있습니다. 나가시면 저장되지 않습니다.'
                    : '선택한 항목을 삭제합니다.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (confirmKind === 'ocrOverwrite') {
                  await handleDeleteAllMrs();
                  await runOcr();
                  setConfirmKind(null);
                } else if (confirmKind === 'editedOcrOverwrite') {
                  if (!selectedImageId || !pendingEditedOcrSource) {
                    setConfirmKind(null);
                    return;
                  }
                  try {
                    await deleteAllMrs.mutateAsync(selectedImageId);
                    applyEditedOcrRows(pendingEditedOcrSource);
                  } catch (e) {
                    handleApiError(e, toast);
                  } finally {
                    setConfirmKind(null);
                  }
                } else if (confirmKind === 'leave') {
                  if (window.history.length > 1) {
                    router.history.back();
                  } else {
                    navigate({ to: copy.listPath } as never);
                  }
                  setConfirmKind(null);
                } else if (confirmKind === 'deleteAll') {
                  await handleDeleteAllMrs();
                  setConfirmKind(null);
                } else if (confirmKind === 'unverifiable') {
                  await handleMarkUnverifiable();
                  setConfirmKind(null);
                }
              }}
            >
              {confirmKind === 'leave'
                ? '나가기'
                : confirmKind === 'unverifiable'
                  ? '확인불가'
                  : '확인'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
